'use client'

import clsx from 'clsx'
import Link from 'next/link'
import { useState, useEffect, useCallback } from 'react'

import { nextLinkHrefFromPathWithOptionalHash } from '@/_lib/next-link-href'
import { handleProgramaticScroll } from '@/_lib/utils'

import {
	useHeaderNavigation,
	useHeaderOrderOnlineNavigateClick,
	useMegamenuLinks
} from '../../_hooks'

interface AMegamenuProps {
	isOpen: boolean
	onClose: () => void
}

export const AMegamenu = ({ isOpen, onClose }: AMegamenuProps) => {
	const megamenuLinks = useMegamenuLinks()
	const { isLinkActive, isParentLinkActive } = useHeaderNavigation()
	const onOrderOnlinePromoClick = useHeaderOrderOnlineNavigateClick(() => onClose())
	const [openedMegamenuLink, setOpenedMegamenuLink] = useState<string | null>(null)

	// Auto-open first link with children when menu opens
	useEffect(() => {
		if (isOpen) {
			const firstLinkWithChildren = megamenuLinks.find(link => link.children?.length)
			if (firstLinkWithChildren) {
				setOpenedMegamenuLink(firstLinkWithChildren.label)
			}
		}
	}, [isOpen, megamenuLinks])

	const handleMegamenuLinkClick = useCallback(
		(link: { label: string; children?: unknown[] }) => {
			if (openedMegamenuLink === link.label) {
				setOpenedMegamenuLink(null)
			} else {
				setOpenedMegamenuLink(link.label)
			}

			if (!link.children) {
				onClose()
			}
		},
		[openedMegamenuLink, onClose]
	)

	return (
		<div className={`megamenu ${isOpen ? 'is-open' : ''}`}>
			<div className='megamenu_scroll'>
				<div className='megamenu_inner wrapper'>
					<div className='megamenu_top'>
						<div className='boomex-grid'>
							<nav className='col52p col1_lgm'>
								<ul className='megamenu_menu'>
									{megamenuLinks.map(link => (
										<li
											key={link.label}
											className={clsx(
												isParentLinkActive(
													link.to,
													!!link.children,
													link.children
												) &&
													link.highlight !== false &&
													'active',
												link.children?.length && 'has-children',
												openedMegamenuLink === link.label && 'expanded'
											)}
										>
											<Link
												title={link.label}
												href={
													link.children ? '#' : nextLinkHrefFromPathWithOptionalHash(link.to)
												}
												onClick={() => handleMegamenuLinkClick(link)}
												prefetch={link.prefetch ?? false}
											>
												<span>{link.label}</span>

												{link.children && (
													<span>
														<svg className='sico'>
															<use href='/assets/layout/icons.svg#s_ar1_d' />
														</svg>
													</span>
												)}
											</Link>

											{link.children && openedMegamenuLink === link.label && (
												<div>
													<ul>
														{link.children.map(child => (
															<li
																key={child.label}
																className={
																	isLinkActive(child) &&
																	link.highlight !== false
																		? 'active'
																		: ''
																}
															>
																<Link
																	href={nextLinkHrefFromPathWithOptionalHash(child.to)}
																	prefetch={child.prefetch}
																	onClick={e => {
																		handleProgramaticScroll(e)
																		onClose()
																	}}
																	title={child.label}
																>
																	{child.label}
																</Link>
															</li>
														))}
													</ul>
												</div>
											)}
										</li>
									))}
								</ul>
							</nav>

							<div className='col48p col1_lgm mgt-30lg pdb-50lg pdt-20 order-first-lgm'>
								<div>
									<h2 className='title_h11'>Objednajte sa</h2>

									<div className='page_content reset_margins mgt-30'>
										<p>
											Vaše zdravie je pre nás prioritou, preto je získanie
											termínu v našej poliklinike rýchle a jednoduché. A vždy
											bude.
										</p>
									</div>

									<Link
										href='/volne-terminy'
										title='Objednajte sa'
										className='btn_basic type2 mgt-30 mgt-30lg'
										onClick={onOrderOnlinePromoClick}
									>
										<span>Objednajte sa online</span>
										<span>
											<span>
												<svg className='sico'>
													<use href='/assets/layout/icons.svg#s_ar2_r' />
												</svg>
											</span>
											<span>
												<svg className='sico'>
													<use href='/assets/layout/icons.svg#s_ar2_r' />
												</svg>
											</span>
										</span>
									</Link>
								</div>
							</div>
						</div>
					</div>

					<div className='megamenu_bottom'>
						<div className='boomex-grid text_center_mdm'>
							<div className='megamenu_contact no_margins col22p col2_lgm col1_mdm'>
								<p>
									<strong>Adresa:</strong>
								</p>
								<p>Poliklinika Medante</p>
								<p>GBC 5, Galvaniho 19, Bratislava</p>
							</div>

							<div className='megamenu_contact no_margins col30p col2_lgm col1_mdm mgt-20lmd'>
								<p>
									<strong>Otváracie hodiny:</strong>
								</p>
								<p>Pondelok - Piatok: 7:00 - 17:00</p>
							</div>

							<div className='megamenu_contact no_margins col24p col2_lgm col1_mdm mgt-20lg'>
								<p>
									<strong>Klientske centrum:</strong>
								</p>
								<p>
									Tel. č.:{' '}
									<Link
										href='tel:+421220302303'
										title='Tel. č.: +421 2 20 302 303'
										prefetch={false}
									>
										+421&nbsp;2&nbsp;20&nbsp;302&nbsp;303
									</Link>
								</p>
								<p>
									E-mail:{' '}
									<Link
										href='mailto:recepcia@medante.sk'
										title='E-mail: recepcia@medante.sk'
										prefetch={false}
									>
										recepcia@medante.sk
									</Link>
								</p>
							</div>
						</div>

						<div className='boomex-grid mid-space mgt-60'>
							<div className='col52p col1_lgm text_center_lgm'>
								<div className='boomex-grid align-center flex-nowrap-lg'>
									<div className='col_default col1_lgm'>
										<Link
											href='mailto:recepcia@medante.sk'
											title='Napíšte nám'
											prefetch={false}
										>
											<span className='text_ul'>Napíšte nám</span>
										</Link>
										, ako ste boli spokojní:
									</div>
								</div>
							</div>

							<div className='col48p col1_lgm'>
								<div className='boomex-grid mid-space align-center flex-nowrap-lg'>
									<div className='col2 col1_lgm mgt-30lg text_center_lgm'></div>

									<div className='col2 col1_lgm mgt-30lg'>
										<div className='elm_social justify-center w100p_lgm'>
											<Link
												href='https://www.facebook.com/klinikamedante'
												title='Facebook'
												target='_blank'
												className='fb'
											>
												<svg className='sicofill'>
													<use href='/assets/layout/icons.svg#s_fb'></use>
												</svg>
											</Link>

											<Link
												href='https://www.instagram.com/poliklinika_medante/'
												title='Instagram'
												target='_blank'
												className='insta'
												prefetch={false}
											>
												<svg className='sicofill'>
													<use href='/assets/layout/icons.svg#s_insta'></use>
												</svg>
											</Link>

											<Link
												href='https://www.linkedin.com/company/medante-poliklinika-celostnej-medic%C3%ADny/'
												title='LinkedIn'
												target='_blank'
												className='in'
												prefetch={false}
											>
												<svg className='sicofill'>
													<use href='/assets/layout/icons.svg#s_in'></use>
												</svg>
											</Link>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<button
				type='button'
				title='Zavrieť menu'
				name='toggle-menu'
				className='megamenu_close cursor-pointer'
				onClick={onClose}
			>
				<svg className='sicofill'>
					<use href='/assets/layout/icons.svg#s_cross' />
				</svg>
			</button>
		</div>
	)
}
